/* New leave request (HR on behalf of an employee) with AI fill + attachment. */
import { useId, useState } from "react";
import type { LeavePolicy } from "../../../data/types";
import { ApiError } from "../../../data/provider";
import { useAiExtract, useAllEmployees, useCreateLeave, useLeaveBalance, useUploadFile } from "../../../data/hooks";
import { AiNote, Alert, Button, Field, Icon, Input, Modal, Select, TextField, errMsg, useToast } from "../../../ui";
import { useI18n } from "../../../i18n";
import { hDate } from "../../../lib/format";
import { addDays, inclusiveDays, isISODate } from "../../../domain/dates";

export function LeaveRequestModal({ policies, today, onClose, aiFirst }: { policies: LeavePolicy[]; today: string; onClose: () => void; aiFirst?: boolean }) {
  const i = useI18n();
  const { L, t } = i;
  const toast = useToast();
  const emps = useAllEmployees();
  const create = useCreateLeave();
  const ai = useAiExtract();
  const upload = useUploadFile();
  const fileId = useId();
  const active = (emps.data ?? []).filter((e) => e.status !== "Terminated");
  const [emp, setEmp] = useState<number>(106);
  const [type, setType] = useState(policies[0]?.type ?? "Annual");
  const [from, setFrom] = useState(addDays(today, 36));
  const [to, setTo] = useState(addDays(today, 40));
  const [note, setNote] = useState("");
  const [att, setAtt] = useState<{ key: string; name: string } | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [conflict, setConflict] = useState("");
  const [aiOpen, setAiOpen] = useState(!!aiFirst);
  const [aiText, setAiText] = useState("");
  const [aiConf, setAiConf] = useState<number | null>(null);
  const empId = active.some((e) => e.id === emp) ? emp : active[0]?.id ?? emp;
  const bal = useLeaveBalance(empId, { enabled: empId > 0 });
  const days = isISODate(from) && isISODate(to) ? inclusiveDays(from, to) : 0;
  const clear = () => { setErrs({}); setConflict(""); };

  const runAi = () => {
    if (!aiText.trim() && !att) { setErrs({ ai: L("Paste a message or attach a medical report first", "الصق رسالة أو أرفق تقريرًا طبيًا أولًا") }); return; }
    ai.mutate({ feature: "leave", text: aiText.trim() || undefined, fileKey: att?.key }, {
      onSuccess: (r) => {
        const f = r.fields;
        if (typeof f.type === "string" && policies.some((p) => p.type === f.type)) setType(f.type);
        if (typeof f.from === "string") setFrom(f.from);
        if (typeof f.to === "string") setTo(f.to);
        if (typeof f.note === "string") setNote(f.note);
        setAiConf(r.confidence);
        clear();
        toast(L("Leave request prepared — check the dates and submit", "تم تجهيز طلب الإجازة — راجع التواريخ وأرسل"));
      },
      onError: (e) => toast(errMsg(e), "bad"),
    });
  };

  const onFile = (f: File | undefined) => {
    if (!f) return;
    upload.mutate({ file: f, name: f.name, folder: "leave" }, {
      onSuccess: (r) => { setAtt({ key: r.key, name: r.name }); toast(L("Attachment uploaded", "تم رفع المرفق")); },
      onError: (e) => toast(errMsg(e), "bad"),
    });
  };

  const submit = () => {
    const e: Record<string, string> = {};
    if (!isISODate(from)) e.from = L("Pick a start date", "اختر تاريخ البداية");
    if (!isISODate(to)) e.to = L("Pick an end date", "اختر تاريخ النهاية");
    else if (isISODate(from) && to < from) e.to = L("Must be on or after the start date", "يجب أن يكون في تاريخ البداية أو بعده");
    setErrs(e); setConflict("");
    if (Object.keys(e).length) return;
    create.mutate({ employeeId: empId, type, from, to, note: note.trim() || undefined, attachmentKey: att?.key }, {
      onSuccess: (l) => { toast(L(`Leave request created · ${l.days} days`, `تم إنشاء طلب الإجازة · ${l.days} يوم`)); onClose(); },
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409) { setConflict(err.message); return; }
        if (err instanceof ApiError && err.fields) {
          const f = { ...err.fields };
          if (f.days === "exceeds balance") f.days = L("More days than the employee's remaining annual balance", "عدد الأيام يتجاوز رصيد الإجازة السنوية المتبقي");
          setErrs(f); return;
        }
        toast(errMsg(err), "bad");
      },
    });
  };

  return (
    <Modal title={t("New request")} onClose={onClose} testId="leave-modal"
      foot={<><Button onClick={onClose}>{t("Cancel")}</Button><Button variant="pri" onClick={submit} loading={create.isPending} data-testid="leave-submit">{t("Submit")}</Button></>}>
      <div className="stack">
        <div className="aibar" style={{ marginBottom: 0 }}>
          <Icon name="sparkle" />
          <div><b>{L("Leave from a message or medical report", "الإجازة من رسالة أو تقرير طبي")}</b><span className="xs muted">{L("Paste a message like “leave 12–26 Oct, family visit” or attach a Sehhaty sick note.", "الصق رسالة مثل «إجازة 12–26 أكتوبر لزيارة العائلة» أو أرفق تقرير صحتي.")}</span></div>
          {!aiOpen && <Button className="aibtn" icon="sparkle" onClick={() => setAiOpen(true)} data-testid="btn-ai-open">{L("Fill from message", "تعبئة من رسالة")}</Button>}
        </div>
        {aiOpen && (
          <div className="stack ai-panel" data-testid="ai-panel">
            <Field label={L("Message or report text", "نص الرسالة أو التقرير")} htmlFor="ai-text" err={errs.ai}>
              <textarea id="ai-text" className="inp" value={aiText} onChange={(e) => { setAiText(e.target.value); setErrs((x) => ({ ...x, ai: "" })); }} placeholder={L("e.g. I need annual leave from 12 to 26 October for a family visit", "مثال: أحتاج إجازة سنوية من 12 إلى 26 أكتوبر لزيارة العائلة")} data-testid="ai-text" />
            </Field>
            <div><Button className="aibtn" icon="sparkle" onClick={runAi} loading={ai.isPending} data-testid="btn-ai-run">{L("Fill the request", "تعبئة الطلب")}</Button></div>
            {aiConf != null && <AiNote>{L(`Filled by AI · ${Math.round(aiConf * 100)}% confidence — please check the dates.`, `تمت التعبئة بالذكاء الاصطناعي · ثقة ${Math.round(aiConf * 100)}% — يرجى مراجعة التواريخ.`)}</AiNote>}
          </div>
        )}
        {conflict && <Alert tone="bad" title={L("Overlaps another leave", "يتداخل مع إجازة أخرى")} desc={conflict} testId="leave-conflict" />}
        {errs.days && <Alert tone="bad" title={errs.days} testId="leave-balance-error" />}
        <Field label={t("Employee")} htmlFor="lv-emp">
          <Select id="lv-emp" value={empId} onChange={(e) => { setEmp(Number(e.target.value)); clear(); }} options={active.map((e): [number, string] => [e.id, i.name(e) + " · " + e.code])} data-testid="lv-emp" />
        </Field>
        <Field label={t("Type")} htmlFor="lv-type" err={errs.type} hint={policies.find((p) => p.type === type) ? L(policies.find((p) => p.type === type)!.en, policies.find((p) => p.type === type)!.ar) : undefined}>
          <Select id="lv-type" value={type} onChange={(e) => { setType(e.target.value); clear(); }} options={policies.map((p): [string, string] => [p.type, t(p.type)])} data-testid="lv-type" />
        </Field>
        <div className="form2">
          <Field label={t("From")} htmlFor="lv-from" err={errs.from} hint={hDate(from)}><Input id="lv-from" type="date" value={from} onChange={(e) => { setFrom(e.target.value); clear(); }} bad={!!errs.from} data-testid="lv-from" /></Field>
          <Field label={t("To")} htmlFor="lv-to" err={errs.to} hint={hDate(to)}><Input id="lv-to" type="date" value={to} onChange={(e) => { setTo(e.target.value); clear(); }} bad={!!errs.to} data-testid="lv-to" /></Field>
        </div>
        <div className="stat-line" data-testid="lv-summary">
          <span>{t("Days")} <b>{days}</b></span>
          {type === "Annual" && bal.data && <span>{L("Annual balance left", "الرصيد السنوي المتبقي")} <b data-testid="lv-balance">{bal.data.remaining}</b></span>}
          {type === "Annual" && bal.data && days > bal.data.remaining && <Chip warn>{L("Exceeds balance", "يتجاوز الرصيد")}</Chip>}
        </div>
        <TextField label={L("Note", "ملاحظة")} value={note} onChange={setNote} testId="lv-note" />
        <div className="row">
          <label className="btn sm" htmlFor={fileId}>{upload.isPending ? <span className="spin" aria-hidden="true" /> : <Icon name="upload" />}<span>{L("Attach medical report / document", "إرفاق تقرير طبي / مستند")}</span></label>
          <input id={fileId} type="file" className="sr" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic" aria-label={L("Attachment", "المرفق")} data-testid="lv-file" onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} />
          {att && <span className="chip ok dot" data-testid="lv-attachment">{att.name}</span>}
          {att && <Button variant="ghost sm" onClick={() => setAtt(null)} data-testid="lv-attachment-remove">{L("Remove", "إزالة")}</Button>}
        </div>
      </div>
    </Modal>
  );
}

function Chip({ warn, children }: { warn?: boolean; children: React.ReactNode }) {
  return <span className={"chip dot" + (warn ? " warn" : "")}>{children}</span>;
}
